import React, { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, Mail, Lock, ArrowRight, GraduationCap, BookOpen, DollarSign } from 'lucide-react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SocialAuthentication from "../components/SocialAuthentication";
import config from '../config/config';

/* ─── Animated Hero Canvas ─── */
function HeroCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;

    // Floating particles (books, coins, stars)
    const particles = Array.from({ length: 40 }, (_, i) => ({
      x: Math.random() * 800,
      y: Math.random() * 900,
      size: 2 + Math.random() * 5,
      speed: 0.15 + Math.random() * 0.4,
      drift: (Math.random() - 0.5) * 0.3,
      opacity: 0.08 + Math.random() * 0.2,
      type: i % 3, // 0=dot, 1=cross, 2=diamond
      phase: Math.random() * Math.PI * 2,
    }));

    // Grid lines
    const gridCols = 8;
    const gridRows = 12;

    function draw() {
      t += 0.006;
      const w = W(), h = H();
      if (!w || !h || !isFinite(w) || !isFinite(h)) {
        raf = requestAnimationFrame(draw);
        return;
      }

      ctx.clearRect(0, 0, w, h);

      // Deep navy/forest background
      const bg = ctx.createLinearGradient(0, 0, w * 0.4, h);
      bg.addColorStop(0, "#0f1f3d");
      bg.addColorStop(0.5, "#162447");
      bg.addColorStop(1, "#0d1b2a");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Perspective grid (academic feel)
      ctx.save();
      ctx.globalAlpha = 0.06;
      ctx.strokeStyle = "#4a9eff";
      ctx.lineWidth = 0.8;
      const cellW = w / gridCols;
      const cellH = h / gridRows;
      for (let i = 0; i <= gridCols; i++) {
        ctx.beginPath();
        ctx.moveTo(i * cellW, 0);
        ctx.lineTo(i * cellW, h);
        ctx.stroke();
      }
      for (let j = 0; j <= gridRows; j++) {
        ctx.beginPath();
        ctx.moveTo(0, j * cellH);
        ctx.lineTo(w, j * cellH);
        ctx.stroke();
      }
      ctx.restore();

      // Radial accent glows
      const glowPositions = [
        { x: w * 0.15, y: h * 0.25, color: "#1a56db", r: 200 },
        { x: w * 0.8, y: h * 0.6, color: "#16a34a", r: 160 },
        { x: w * 0.5, y: h * 0.85, color: "#7c3aed", r: 140 },
      ];
      glowPositions.forEach(({ x, y, color, r }) => {
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, color + "22");
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      });

      // Diagonal accent lines
      ctx.save();
      ctx.globalAlpha = 0.07;
      for (let i = -3; i < 6; i++) {
        const xStart = w * i * 0.22 + Math.sin(t * 0.3 + i) * 20;
        ctx.beginPath();
        ctx.moveTo(xStart, 0);
        ctx.lineTo(xStart + h * 0.6, h);
        ctx.strokeStyle = "#4a9eff";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.restore();

      // Animated particles
      particles.forEach(p => {
        p.y -= p.speed;
        p.x += p.drift + Math.sin(t * 0.5 + p.phase) * 0.3;
        if (p.y + p.size < 0) { p.y = h + p.size; p.x = Math.random() * w; }

        const alpha = p.opacity * (0.7 + 0.3 * Math.sin(t * 2 + p.phase));
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);

        if (p.type === 0) {
          ctx.beginPath();
          ctx.arc(0, 0, p.size, 0, Math.PI * 2);
          ctx.fillStyle = "#4a9eff";
          ctx.fill();
        } else if (p.type === 1) {
          // Plus/cross (academic)
          ctx.strokeStyle = "#22c55e";
          ctx.lineWidth = 1.2;
          ctx.beginPath(); ctx.moveTo(-p.size, 0); ctx.lineTo(p.size, 0); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(0, -p.size); ctx.lineTo(0, p.size); ctx.stroke();
        } else {
          // Diamond (finance)
          ctx.rotate(Math.PI / 4);
          ctx.strokeStyle = "#f59e0b";
          ctx.lineWidth = 1;
          ctx.strokeRect(-p.size * 0.7, -p.size * 0.7, p.size * 1.4, p.size * 1.4);
        }
        ctx.restore();
      });

      // Flowing arc (mortarboard curve)
      ctx.save();
      ctx.globalAlpha = 0.12;
      ctx.strokeStyle = "#4a9eff";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let x = 0; x <= w; x += 3) {
        const y = h * 0.5 + Math.sin(x * 0.008 + t * 0.6) * 40 + Math.cos(x * 0.014 + t * 0.4) * 20;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();

      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

/* ─── SEFAMS Loader ─── */
function SefamsLoader() {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18 }}>
      <div style={{ position: "relative", width: 60, height: 60 }}>
        <svg viewBox="0 0 60 60" style={{ position: "absolute", inset: 0, animation: "spinRing 1.2s linear infinite" }}>
          <circle cx="30" cy="30" r="26" fill="none" stroke="rgba(26,86,219,0.15)" strokeWidth="3" />
          <circle cx="30" cy="30" r="26" fill="none" stroke="url(#loaderGrad)" strokeWidth="3"
            strokeDasharray="55 108" strokeLinecap="round" />
          <defs>
            <linearGradient id="loaderGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#1a56db" />
              <stop offset="100%" stopColor="#16a34a" />
            </linearGradient>
          </defs>
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GraduationCap size={22} color="#1a56db" style={{ animation: "iconPulse 1.2s ease-in-out infinite" }} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        {"SEFAMS".split("").map((ch, i) => (
          <span key={i} style={{
            fontFamilky: "'DM Serif Display', serif",
            fontWeight: 400, fontSize: 16, color: "#0f1f3d",
            animation: "letterBounce 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.08}s`, display: "inline-block", letterSpacing: "0.12em",
          }}>{ch}</span>
        ))}
      </div>
      <span style={{ fontFamilky: "'DM Sans', sans-serif", fontSize: 11, color: "rgba(26,86,219,0.5)", letterSpacing: "0.15em", textTransform: "uppercase" }}>
        Authenticating…
      </span>
    </div>
  );
}

/* ─── Main LoginForm ─── */
function LoginForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [roleHint, setRoleHint] = useState(0); // cycles through roles

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    setTimeout(() => setMounted(true), 80);

    const interval = setInterval(() => setRoleHint(h => (h + 1) % 3), 2800);
    return () => {
      document.head.removeChild(link);
      clearInterval(interval);
    };
  }, []);

  const roles = ["Students", "Registrar", "Finance Officers"];
  const roleColors = ["#1a56db", "#16a34a", "#d97706"];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null); setSuccess(null);
    try {
      await new Promise(r => setTimeout(r, 1500));
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
        return match ? decodeURIComponent(match[2]) : null;
      };
      const xsrfToken = getCookie("XSRF-TOKEN");
      const response = await axios.post(config.API_URL + "/api/apiLogin", formData, {
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-XSRF-TOKEN": xsrfToken },
        withCredentials: true,
      });
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("userInfor", response.data.user);
      if (response.data.status == 1) {
        setSuccess("Successfully logged in");
        
        if (response.data.user == "Super Admin") document.getElementById('formLogin').submit();
        else if (response.data.user == "Teacher") window.location.assign(config.WEB_URL+'/loginUser/'+response.data.token);
        else window.location.assign(config.WEB_URL+'/loginUser/'+response.data.token);
      } else {
        setError("Invalid credentials. Please try again.");
        navigate("/");
      }
    } catch {
      setError("Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');

    * { box-sizing: border-box; }

    @keyframes spinRing { to { transform: rotate(360deg); } }
    @keyframes iconPulse { 0%,100%{opacity:.6;transform:scale(1)} 50%{opacity:1;transform:scale(1.15)} }
    @keyframes letterBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
    @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
    @keyframes floatIcon { 0%,100%{transform:translateY(0) rotate(-2deg)} 50%{transform:translateY(-14px) rotate(2deg)} }
    @keyframes pulseGlow { 0%,100%{opacity:.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.05)} }
    @keyframes roleSlide { 0%{opacity:0;transform:translateY(8px)} 15%{opacity:1;transform:translateY(0)} 85%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(-8px)} }
    @keyframes shimmer { from{left:-100%} to{left:200%} }
    @keyframes badgePop { from{opacity:0;transform:scale(0.8) translateY(8px)} to{opacity:1;transform:scale(1) translateY(0)} }

    .sefams-input {
      width: 100%;
      background: rgba(15,31,61,0.03);
      border: 1.5px solid #e2e8f5;
      border-radius: 10px;
      color: #0f1f3d;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      padding: 12px 16px 12px 46px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    }
    .sefams-input::placeholder { color: #94a3c0; }
    .sefams-input:focus {
      border-color: #1a56db;
      box-shadow: 0 0 0 3px rgba(26,86,219,0.1);
      background: #fff;
    }
    .sefams-input:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 100px #fff inset !important;
      -webkit-text-fill-color: #0f1f3d !important;
    }

    .sefams-btn {
      width: 100%; border: none; cursor: pointer;
      background: linear-gradient(135deg, #1a56db 0%, #1e40af 60%, #16a34a 100%);
      background-size: 200% 100%;
      color: #fff;
      font-family: 'DM Sans', sans-serif;
      font-weight: 600;
      font-size: 14.5px;
      letter-spacing: 0.06em;
      padding: 13px 24px;
      border-radius: 10px;
      display: flex; align-items: center; justify-content: center; gap: 10px;
      box-shadow: 0 4px 20px rgba(26,86,219,0.3), 0 1px 3px rgba(0,0,0,0.1);
      transition: transform 0.15s, box-shadow 0.15s, background-position 0.4s;
      position: relative; overflow: hidden;
    }
    .sefams-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 28px rgba(26,86,219,0.45);
      background-position: right center;
    }
    .sefams-btn:active:not(:disabled) { transform: translateY(0); }
    .sefams-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .sefams-btn::after {
      content: '';
      position: absolute;
      top: 0; left: -100%; width: 60%; height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
      animation: shimmer 2.5s ease infinite;
    }

    .field-icon {
      position: absolute; left: 15px; top: 50%;
      transform: translateY(-50%); pointer-events: none;
      transition: color 0.2s;
    }

    .role-badge {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 3px 10px; border-radius: 20px;
      font-family: 'DM Sans', sans-serif; font-size: 11px; font-weight: 600;
      letter-spacing: 0.08em; text-transform: uppercase;
      animation: roleSlide 2.8s ease-in-out infinite;
    }

    .hero-stat {
      animation: fadeUp 0.7s ease both;
    }

    .module-chip {
      display: flex; align-items: center; gap: 8px;
      padding: 10px 14px; border-radius: 10px;
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.08);
      backdrop-filter: blur(8px);
      transition: background 0.2s, border-color 0.2s;
    }
    .module-chip:hover {
      background: rgba(255,255,255,0.09);
      border-color: rgba(255,255,255,0.14);
    }

    @media(min-width:900px) { .hero-panel { display: flex !important; } }
    .hero-panel { display: none; }
    @media(max-width:899px) { .mobile-brand { display: flex !important; } }
    .mobile-brand { display: none; }
  `;

  const modules = [
    { icon: <BookOpen size={14} color="#4a9eff" />, label: "Learning Management", sub: "Courses · Assignments · Grades" },
    { icon: <DollarSign size={14} color="#22c55e" />, label: "Finance Integration", sub: "Fees · Payments · Reports" },
    { icon: <GraduationCap size={14} color="#f59e0b" />, label: "Student Portal", sub: "Enrollment · Records · ID" },
  ];

  return (
    <>
      <style>{styles}</style>
      <div style={{
        display: "flex", minHeight: "100vh", width: "100%",
        fontFamilky: "'DM Sans', sans-serif",
        background: "#f8fafd",
      }}>
      <form id="formLogin" method="get" action={config.WEB_URL+`/loginUser/`+localStorage.getItem("token")+`/`+formData.email+`/`+formData.password}>
              <input
                      type="hidden" name="email" placeholder="you@school.edu"
                      autoComplete="email" value={formData.email}
                     
                    />
                    <input
                      type="hidden" name="password"
                      placeholder="Enter your password" autoComplete="current-password"
                      value={formData.password}
                    />
              </form>
        {/* ── LEFT: Hero Panel ── */}
        <div
          className="hero-panel"
          style={{
            flex: "0 0 50%", position: "relative", overflow: "hidden",
            flexDirection: "column",
          }}
        >
          <div style={{ position: "absolute", inset: 0 }}>

            <HeroCanvas />
          </div>

          {/* Content */}
          <div style={{
            position: "relative", zIndex: 2,
            display: "flex", flexDirection: "column",
            justifyContent: "center",
            padding: "60px 52px", height: "100%",
          }}>

            {/* Brand Mark */}
            <div style={{ marginBottom: 52 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 14,
                  background: "linear-gradient(135deg, #1a56db, #16a34a)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 4px 20px rgba(26,86,219,0.4)",
                  animation: "floatIcon 5s ease-in-out infinite",
                }}>
                  <GraduationCap size={24} color="#fff" />
                </div>
                <div>
                  <div style={{ fontFamilky: "'DM Serif Display', serif", fontSize: 26, color: "#fff", letterSpacing: "0.08em", lineHeight: 1 }}>
                    SEFAMS
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(160,200,255,0.55)", letterSpacing: "0.2em", textTransform: "uppercase", marginTop: 3 }}>
                    School Education & Finance
                  </div>
                </div>
              </div>

              {/* Headline */}
              <h1 style={{
                fontFamilky: "'DM Serif Display', serif",
                fontWeight: 400, fontSize: "clamp(30px, 3.2vw, 44px)",
                color: "#fff", lineHeight: 1.2, margin: "0 0 14px",
                textShadow: "0 2px 30px rgba(0,0,0,0.3)",
              }}>
                One platform for<br />
                <span style={{
                  background: "linear-gradient(90deg, #4a9eff, #22d3ee)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>learning</span>
                {" "}and{" "}
                <span style={{
                  background: "linear-gradient(90deg, #22c55e, #86efac)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>finance.</span>
              </h1>

              {/* Role cycling */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: "rgba(160,200,255,0.6)", fontWeight: 400 }}>For</span>
                <div key={roleHint} className="role-badge" style={{
                  background: roleColors[roleHint] + "28",
                  border: `1px solid ${roleColors[roleHint]}44`,
                  color: roleColors[roleHint] === "#1a56db" ? "#7db8ff" : roleColors[roleHint] === "#16a34a" ? "#86efac" : "#fcd34d",
                }}>
                  {roles[roleHint]}
                </div>
              </div>
              <p style={{
                color: "rgba(150,190,255,0.6)", fontSize: 14, lineHeight: 1.75,
                maxWidth: 360, margin: "10px 0 0", fontWeight: 300,
              }}>
                Manage courses, track academic progress, process fee collections, and generate financial reports — all in one unified system.
              </p>
            </div>

            {/* Module chips */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {modules.map(({ icon, label, sub }, i) => (
                <div key={label} className="module-chip hero-stat" style={{ animationDelay: `${i * 0.12}s` }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: "rgba(255,255,255,0.07)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>{icon}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(220,235,255,0.85)", letterSpacing: "0.04em" }}>{label}</div>
                    <div style={{ fontSize: 10, color: "rgba(130,170,230,0.5)", marginTop: 1 }}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Version badge */}
            <div style={{
              marginTop: 40,
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "6px 12px", borderRadius: 8,
              background: "rgba(26,86,219,0.15)",
              border: "1px solid rgba(74,158,255,0.2)",
              width: "fit-content",
              animation: "badgePop 0.6s ease 0.5s both",
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e" }} />
              <span style={{ fontSize: 11, color: "rgba(160,210,255,0.7)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                All Systems Operational
              </span>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Login Form ── */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          minHeight: "100vh", padding: "40px 24px",
          background: "#ffffff",
          position: "relative", overflow: "hidden",
        }}>
          {/* Subtle top accent bar */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: "linear-gradient(90deg, #1a56db 0%, #16a34a 50%, #d97706 100%)",
          }} />

          {/* Background texture */}
          <div style={{
            position: "absolute", top: "30%", left: "50%", transform: "translateX(-50%)",
            width: 500, height: 500,
            background: "radial-gradient(ellipse, rgba(26,86,219,0.04) 0%, transparent 70%)",
            pointerEvents: "none", animation: "pulseGlow 6s ease-in-out infinite",
          }} />

          <div style={{
            width: "100%", maxWidth: 400, position: "relative", zIndex: 1,
            opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.55s ease, transform 0.55s ease",
          }}>

            {/* Mobile brand header */}
            <div className="mobile-brand" style={{
              flexDirection: "column", alignItems: "center",
              textAlign: "center", marginBottom: 32, gap: 8,
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: 16,
                background: "linear-gradient(135deg, #1a56db, #16a34a)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 20px rgba(26,86,219,0.3)", marginBottom: 4,
              }}>
                <GraduationCap size={26} color="#fff" />
              </div>
              <div style={{ fontFamilky: "'DM Serif Display', serif", fontSize: 22, color: "#0f1f3d", letterSpacing: "0.08em" }}>SEFAMS</div>
              <div style={{ fontSize: 11, color: "#94a3c0", letterSpacing: "0.16em", textTransform: "uppercase" }}>
                School Education & Finance
              </div>
            </div>

            {/* Card */}
            <div style={{
              background: "#fff",
              border: "1px solid #e8edf8",
              borderRadius: 20,
              padding: "36px 32px",
              boxShadow: "0 8px 40px rgba(15,31,61,0.07), 0 1px 4px rgba(0,0,0,0.04)",
              position: "relative", overflow: "hidden",
            }}>
              {/* Card top accent */}
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, height: 2,
                background: "linear-gradient(90deg, #1a56db, #16a34a)",
                borderRadius: "20px 20px 0 0",
              }} />

              {/* Header */}
              <div style={{ marginBottom: 26 }}>
                <h2 style={{
                  margin: "0 0 5px",
                  fontFamilky: "'DM Serif Display', serif",
                  fontWeight: 400, fontSize: 23, color: "#0f1f3d",
                  letterSpacing: "-0.01em",
                }}>Welcome back</h2>
                <p style={{ margin: 0, fontSize: 13, color: "#8a9ab8", fontWeight: 400 }}>
                  Sign in to access your SEFAMS portal
                </p>
              </div>

              {/* Social Auth */}
              <div style={{ marginBottom: 20 }}>
                <SocialAuthentication />
              </div>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div style={{ flex: 1, height: 1, background: "#edf1f9" }} />
                <span style={{ fontSize: 11, color: "#b0bdd0", letterSpacing: "0.12em", textTransform: "uppercase" }}>or continue with email</span>
                <div style={{ flex: 1, height: 1, background: "#edf1f9" }} />
              </div>

              {/* Loader overlay */}
              {loading && (
                <div style={{
                  position: "absolute", inset: 0, borderRadius: 20, zIndex: 10,
                  background: "rgba(255,255,255,0.94)", backdropFilter: "blur(10px)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <SefamsLoader />
                </div>
              )}

              {/* Alerts */}
              {success && (
                <div style={{
                  marginBottom: 16, padding: "11px 14px", borderRadius: 9,
                  background: "#f0fdf4", border: "1px solid #bbf7d0",
                  display: "flex", alignItems: "center", gap: 9,
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2"/><path d="M8 12l3 3 5-5" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/></svg>
                  <span style={{ fontSize: 13, color: "#15803d", fontWeight: 500, fontFamilky: "'DM Sans', sans-serif" }}>{success}</span>
                </div>
              )}
              {error && (
                <div style={{
                  marginBottom: 16, padding: "11px 14px", borderRadius: 9,
                  background: "#fff5f5", border: "1px solid #fecaca",
                  display: "flex", alignItems: "center", gap: 9,
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2"/><path d="M12 8v4m0 4h.01" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/></svg>
                  <span style={{ fontSize: 13, color: "#991b1b", fontWeight: 500, fontFamilky: "'DM Sans', sans-serif" }}>{error}</span>
                </div>
              )}



              {/* Form */}
              <div style={{ display: "flex", flexDirection: "column", gap: 15, position: "relative" }}>
            
                {/* Email */}
                <div>

                  <label style={{
                    display: "block", fontSize: 11, fontWeight: 600,
                    color: "#4b607a", marginBottom: 6,
                    letterSpacing: "0.09em", textTransform: "uppercase",
                    fontFamilky: "'DM Sans', sans-serif",
                  }}>
                    Email / Student ID
                  </label>
                  <div style={{ position: "relative" }}>
                    <span className="field-icon">
                      <Mail size={15} color={focusedField === "email" ? "#1a56db" : "#94a3c0"} />
                    </span>
                    <input
                      type="email" name="email" placeholder="you@school.edu"
                      autoComplete="email" value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      className="sefams-input" required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                    <label style={{
                      fontSize: 11, fontWeight: 600, color: "#4b607a",
                      letterSpacing: "0.09em", textTransform: "uppercase",
                      fontFamilky: "'DM Sans', sans-serif",
                    }}>Password</label>
                    <a href="#" style={{
                      fontSize: 11, color: "#1a56db", fontWeight: 500,
                      textDecoration: "none", fontFamilky: "'DM Sans', sans-serif",
                      letterSpacing: "0.03em",
                    }}
                      onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                      onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                    >Forgot password?</a>
                  </div>
                  <div style={{ position: "relative" }}>
                    <span className="field-icon">
                      <Lock size={15} color={focusedField === "password" ? "#1a56db" : "#94a3c0"} />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"} name="password"
                      placeholder="Enter your password" autoComplete="current-password"
                      value={formData.password} onChange={handleChange}
                      onFocus={() => setFocusedField("password")}
                      onBlur={() => setFocusedField(null)}
                      className="sefams-input" required
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                        color: "#94a3c0", display: "flex", transition: "color 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = "#1a56db"}
                      onMouseLeave={e => e.currentTarget.style.color = "#94a3c0"}
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit" disabled={loading}
                  onClick={handleSubmit}
                  className="sefams-btn"
                  style={{ marginTop: 4 }}
                >
                  {loading ? (
                    <span style={{ fontFamilky: "'DM Sans', sans-serif" }}>Please wait…</span>
                  ) : (
                    <>
                      <span>Sign In to Portal</span>
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>
              </div>

              {/* Module badges */}
              <div style={{ marginTop: 22, display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                {[
                  { label: "LMS", color: "#1a56db" },
                  { label: "Finance", color: "#16a34a" },
                  { label: "Records", color: "#d97706" },
                ].map(({ label, color }) => (
                  <span key={label} style={{
                    padding: "3px 10px", borderRadius: 20,
                    background: color + "12", border: `1px solid ${color}28`,
                    fontSize: 10, fontWeight: 600, color: color,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    fontFamilky: "'DM Sans', sans-serif",
                  }}>{label}</span>
                ))}
              </div>
            </div>

            {/* Footer */}
            <p style={{
              textAlign: "center", marginTop: 22, fontSize: 11,
              color: "#b0bdd0", letterSpacing: "0.05em", fontFamilky: "'DM Sans', sans-serif",
            }}>
              © {new Date().getFullYear()} SEFAMS · School Education & Finance Administration Management System
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default LoginForm;